import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Plane,
  FileText,
  Clock,
  CheckCircle2,
  Wallet,
  AlertCircle,
  Plus,
  ArrowRight,
  Building2,
  Calendar,
  MapPin,
  CheckSquare,
  Inbox,
  Sparkles,
  Receipt,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TravelDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_URL}/travel-desk/dashboard`, { withCredentials: true });
      setDashboardData(data);
    } catch (err) {
      toast.error('Failed to load travel dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    } catch (e) {
      return dateStr;
    }
  };

  const getDestination = (r) => {
    if (r.journey_details && Array.isArray(r.journey_details) && r.journey_details.length > 0) {
      const cities = r.journey_details.map((j) => j.to_city).filter(Boolean);
      if (cities.length > 0) return cities.join(', ');
    }
    if (r.destination) return r.destination;
    if (r.to_city) return r.to_city;
    return r.travel_category || 'N/A';
  };

  const getTravelDate = (r) => {
    if (r.journey_details && Array.isArray(r.journey_details) && r.journey_details.length > 0) {
      const date = r.journey_details[0]?.departure_date;
      if (date) return formatDate(date);
    }
    if (r.departure_date) return formatDate(r.departure_date);
    if (r.created_at) return formatDate(r.created_at);
    return 'N/A';
  };

  const getStatusBadge = (status) => {
    const s = (status || '').toLowerCase();
    switch (s) {
      case 'approved':
      case 'booked':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'completed':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'rejected':
        return 'bg-rose-50 text-rose-700 border-rose-200';
      case 'cancelled':
        return 'bg-slate-100 text-slate-600 border-slate-200';
      case 'pending_approval':
      case 'submitted':
      case 'draft':
      default:
        return 'bg-amber-50 text-amber-700 border-amber-200';
    }
  };

  const formatStatusText = (status) => {
    if (!status) return 'Pending';
    return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  if (loading) {
    return (
      <div className="p-8 flex justify-center items-center min-h-[600px]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm font-semibold text-slate-600">Loading Travel Dashboard...</p>
        </div>
      </div>
    );
  }

  const kpis = dashboardData?.kpis || {};
  const deptSpend = dashboardData?.department_spend || {};
  const recentReqs = dashboardData?.recent_requests || [];

  const completedTripsCount = (recentReqs || []).filter((r) => r.status === 'completed' || r.status === 'booked').length;
  const pendingFinanceCount = kpis.pending_requests || (recentReqs || []).filter((r) => ['submitted', 'pending_approval', 'pending_finance', 'pending_expense'].includes(r.status)).length;

  return (
    <div className="p-6 space-y-6 max-w-[1600px] mx-auto font-sans bg-slate-50/50 min-h-screen">
      {/* Top Banner Header */}
      <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-blue-950 text-white rounded-2xl p-6 shadow-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border border-slate-800">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-amber-500/20 text-amber-400 rounded-xl border border-amber-500/30 shadow-inner">
              <Plane className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-2xl font-black tracking-tight">Travel Desk Dashboard</h1>
              <p className="text-xs text-blue-200">
                Corporate Travel Analytics · Employee Travel Overview · Spend &amp; Trip Status
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <button
            onClick={() => navigate('/travel/request/new')}
            className="px-4 py-2.5 bg-gradient-to-r from-amber-500 to-amber-400 hover:from-amber-400 hover:to-amber-300 text-slate-950 font-bold text-xs rounded-xl shadow-lg flex items-center gap-2 transition-all transform hover:-translate-y-0.5" >
            <Plus className="w-4 h-4" /> Raise Travel Request
          </button>
        </div>
      </div>

      {/* KPI Cards Grid - 6 Employee Relevant Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {/* Card 1: Total Travel Requests */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-md transition-all space-y-2">
          <div className="flex justify-between items-center text-slate-500 text-xs font-bold uppercase tracking-wider">
            <span>Total Requests</span>
            <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
              <FileText className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-black text-slate-900">{kpis.total_requests || 0}</div>
          <div className="text-[11px] text-slate-500 font-medium pt-1 border-t border-slate-100 flex items-center gap-1.5">
            <span className="text-blue-600 font-bold">{kpis.domestic_trips || 0} Domestic</span>
            <span>•</span>
            <span className="text-indigo-600 font-bold">{kpis.international_trips || 0} Intl</span>
          </div>
        </div>

        {/* Card 2: Pending Approvals */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-md transition-all space-y-2">
          <div className="flex justify-between items-center text-slate-500 text-xs font-bold uppercase tracking-wider">
            <span>Pending Approvals</span>
            <div className="p-2 bg-amber-50 text-amber-600 rounded-lg">
              <Clock className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-black text-amber-600">{kpis.pending_requests || 0}</div>
          <div className="text-[11px] text-slate-500 font-medium pt-1 border-t border-slate-100">
            Awaiting manager / HR action
          </div>
        </div>

        {/* Card 3: Approved Requests */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-md transition-all space-y-2">
          <div className="flex justify-between items-center text-slate-500 text-xs font-bold uppercase tracking-wider">
            <span>Approved Requests</span>
            <div className="p-2 bg-emerald-50 text-emerald-600 rounded-lg">
              <CheckCircle2 className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-black text-emerald-600">{kpis.approved_requests || 0}</div>
          <div className="text-[11px] text-slate-500 font-medium pt-1 border-t border-slate-100">
            Ready for booking & travel
          </div>
        </div>

        {/* Card 4: Completed Trips */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-md transition-all space-y-2">
          <div className="flex justify-between items-center text-slate-500 text-xs font-bold uppercase tracking-wider">
            <span>Completed Trips</span>
            <div className="p-2 bg-indigo-50 text-indigo-600 rounded-lg">
              <Plane className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-black text-indigo-600">{completedTripsCount}</div>
          <div className="text-[11px] text-slate-500 font-medium pt-1 border-t border-slate-100">
            Successfully traveled trips
          </div>
        </div>

        {/* Card 5: Total Travel Expenses */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-md transition-all space-y-2">
          <div className="flex justify-between items-center text-slate-500 text-xs font-bold uppercase tracking-wider">
            <span>Total Expenses</span>
            <div className="p-2 bg-teal-50 text-teal-600 rounded-lg">
              <Wallet className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-black text-slate-900 truncate">
            ₹ {(kpis.total_travel_spend || 0).toLocaleString('en-IN')}
          </div>
          <div className="text-[11px] text-slate-500 font-medium pt-1 border-t border-slate-100">
            Confirmed travel spend
          </div>
        </div>

        {/* Card 6: Pending Expense / Finance Review */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-md transition-all space-y-2">
          <div className="flex justify-between items-center text-slate-500 text-xs font-bold uppercase tracking-wider">
            <span>Pending Review</span>
            <div className="p-2 bg-rose-50 text-rose-600 rounded-lg">
              <AlertCircle className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-black text-rose-600">{pendingFinanceCount}</div>
          <div className="text-[11px] text-slate-500 font-medium pt-1 border-t border-slate-100">
            Finance & audit review
          </div>
        </div>
      </div>

      {/* Main Section: Recent Travel Requests & Department Spend */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Travel Requests Table */}
        <div className="lg:col-span-2 bg-white rounded-2xl p-6 border border-slate-200/80 shadow-sm space-y-4 flex flex-col justify-between">
          <div className="space-y-4">
            <div className="flex items-center justify-between pb-2 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
                  <Clock className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 text-sm">Recent Travel Requests</h3>
                  <p className="text-[11px] text-slate-400 font-medium">Track your travel requests and approval state</p>
                </div>
              </div>
              <button
                onClick={() => navigate('/travel/requests')}
                className="text-xs font-bold text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1 bg-blue-50/60 hover:bg-blue-100/70 px-3 py-1.5 rounded-lg transition-all"
              >
                View All <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>

            {recentReqs.length === 0 ? (
              <div className="py-12 flex flex-col items-center justify-center text-center space-y-3">
                <div className="w-12 h-12 rounded-2xl bg-slate-100 text-slate-400 flex items-center justify-center">
                  <Inbox className="w-6 h-6" />
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-bold text-slate-700">No Travel Requests Found</p>
                  <p className="text-xs text-slate-400 max-w-xs">There are no recent travel requests available in your record.</p>
                </div>
                <button
                  onClick={() => navigate('/travel/request/new')}
                  className="px-3.5 py-2 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs rounded-xl shadow-sm flex items-center gap-2 transition-all mt-2"
                >
                  <Plus className="w-3.5 h-3.5" /> Raise New Request
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="bg-slate-50/80 text-slate-500 font-bold uppercase tracking-wider border-y border-slate-100">
                      <th className="py-3 px-3">Request ID</th>
                      <th className="py-3 px-3">Purpose</th>
                      <th className="py-3 px-3">Travel Date</th>
                      <th className="py-3 px-3">Destination</th>
                      <th className="py-3 px-3">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {recentReqs.map((r) => (
                      <tr
                        key={r._id}
                        onClick={() => navigate(`/travel/request/${r._id}`)}
                        className="hover:bg-slate-50/80 transition-colors cursor-pointer group"
                      >
                        <td className="py-3 px-3 font-mono font-bold text-blue-600 group-hover:underline">
                          {r.request_number || r._id.substring(0, 8)}
                        </td>
                        <td className="py-3 px-3">
                          <div className="font-semibold text-slate-900 max-w-[180px] truncate">{r.purpose || 'Business Travel'}</div>
                          <div className="text-[10px] text-slate-400">{r.employee_name || 'Self'}</div>
                        </td>
                        <td className="py-3 px-3 text-slate-600 font-medium whitespace-nowrap">
                          <div className="flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5 text-slate-400" />
                            {getTravelDate(r)}
                          </div>
                        </td>
                        <td className="py-3 px-3 text-slate-600 font-medium">
                          <div className="flex items-center gap-1.5 max-w-[150px] truncate">
                            <MapPin className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                            <span className="truncate">{getDestination(r)}</span>
                          </div>
                        </td>
                        <td className="py-3 px-3 whitespace-nowrap">
                          <span
                            className={`px-2.5 py-1 rounded-full text-[10px] font-bold border capitalize inline-flex items-center gap-1 ${getStatusBadge(
                              r.status
                            )}`}
                          >
                            {formatStatusText(r.status)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="pt-4 border-t border-slate-100 flex items-center justify-between">
            <span className="text-[11px] text-slate-400">Showing recent travel requests</span>
            <button
              onClick={() => navigate('/travel/approval')}
              className="px-3.5 py-2 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs rounded-xl flex items-center gap-2 shadow-sm transition-all"
            >
              <CheckSquare className="w-3.5 h-3.5" /> Go to Approval Inbox
            </button>
          </div>
        </div>

        {/* Department Travel Spend Breakdown */}
        <div className="bg-white rounded-2xl p-6 border border-slate-200/80 shadow-sm space-y-4 flex flex-col justify-between">
          <div className="space-y-4">
            <div className="flex items-center justify-between pb-2 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-emerald-50 text-emerald-600 rounded-lg">
                  <Building2 className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 text-sm">Department Spend</h3>
                  <p className="text-[11px] text-slate-400 font-medium">Travel budget utilization</p>
                </div>
              </div>
              <span className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded font-semibold">All Time</span>
            </div>

            <div className="space-y-4 pt-1">
              {Object.keys(deptSpend).length === 0 ? (
                <div className="py-8 flex flex-col items-center justify-center text-center space-y-2">
                  <Building2 className="w-8 h-8 text-slate-300" />
                  <p className="text-xs text-slate-400 italic">No departmental spend recorded yet.</p>
                </div>
              ) : (
                Object.entries(deptSpend).map(([dept, amt]) => {
                  const total = kpis.total_travel_spend || 1;
                  const pct = Math.min(100, Math.round((amt / total) * 100));
                  return (
                    <div key={dept} className="space-y-1.5">
                      <div className="flex justify-between text-xs font-semibold text-slate-700">
                        <span className="flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block"></span>
                          {dept}
                        </span>
                        <span className="font-mono font-bold text-slate-900">
                          ₹ {amt.toLocaleString('en-IN')} <span className="text-slate-400 text-[10px]">({pct}%)</span>
                        </span>
                      </div>
                      <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-emerald-500 to-teal-400 rounded-full transition-all duration-500"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="pt-4 border-t border-slate-100">
            <div className="p-3 bg-indigo-50/60 rounded-xl border border-indigo-100 flex items-center gap-3">
              <Sparkles className="w-5 h-5 text-indigo-600 shrink-0" />
              <div className="text-[11px] text-indigo-900 font-medium">
                All travel requests are subject to company travel policy & budget limits.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TravelDashboard;
